import axios from 'axios';
import { sessionVersion } from '../../auth/session';
import { apiClient } from '../../api/client';
import { ApiError, ApiResponse } from '../../api/types';
import { BackendErrors, BackendLogKind, BackendLogsPage, Chart, ChartID, ContainerHealth, GameServerMetrics, TimeFilter, TimeWindows, UserMonitor } from './types';

import { getMonitorApiBaseUrl } from '../../config/env';

export async function monitorRequest<T>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', path: string, signal?: AbortSignal, params?: object, data?: unknown): Promise<T> {
  const version = sessionVersion();
  const baseURL = getMonitorApiBaseUrl();
  if (!baseURL) throw new Error('Monitor API is not configured for this environment.');
  try {
    const response = await apiClient.request<T>({ method, url: path, baseURL, signal, params, data, timeout: 30000, withCredentials: false });
    if (version !== sessionVersion()) throw new axios.CanceledError('Session changed');
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) throw error;
    if (axios.isAxiosError<{ error?: string }>(error)) {
      throw new Error(error.response?.data?.error || (error.response
        ? `Monitor request failed (${error.response.status})`
        : 'Cannot reach the Monitor API. Check its address and allowed CORS origin.'));
    }
    throw error;
  }
}

const get = <T,>(path: string, signal?: AbortSignal, params?: TimeFilter) => monitorRequest<T>('GET', path, signal, params);

export const getTimeWindows = (signal?: AbortSignal) => get<TimeWindows>('/api/v1/time-windows', signal);
export const getChart = (id: ChartID, filter: TimeFilter, signal?: AbortSignal) =>
  get<Chart>(`/api/v1/charts/${id}`, signal, filter);
export const getGameServerMetrics = (filter: TimeFilter, environment = '', signal?: AbortSignal) =>
  monitorRequest<GameServerMetrics>('GET', '/api/v1/game-servers/metrics', signal, { ...filter, ...(environment ? { environment } : {}) });

export async function getUserMonitor(signal?: AbortSignal): Promise<UserMonitor> {
  const response = await apiClient.get<ApiResponse<UserMonitor>>('/api/v1/admin/monitor/users', {
    signal,
    timeout: 15000,
  });
  return response.data.data;
}

async function getContainerInventory(path: string, signal?: AbortSignal): Promise<ContainerHealth> {
  let result: ContainerHealth | undefined;
  let page: ContainerHealth;
  do {
    page = await monitorRequest<ContainerHealth>('GET', path, signal,
      { limit: 200, offset: result?.items.length || 0 });
    if (!result) result = { ...page, items: [...page.items] };
    else result.items.push(...page.items);
  } while (page.items.length > 0 && result.items.length < page.total);
  return result;
}

export const getContainers = (signal?: AbortSignal) => getContainerInventory('/api/v1/containers', signal);
export const getContainerHealth = (signal?: AbortSignal) => getContainerInventory('/api/v1/containers/health', signal);

export type BackendAlertTest = number | 'log-error';

export async function triggerBackendAlertTest(kind: BackendAlertTest): Promise<void> {
  if (typeof kind === 'number' && (!Number.isInteger(kind) || kind < 400 || kind > 599)) {
    throw new Error('Choose an integer HTTP status between 400 and 599.');
  }

  const expectedStatus = typeof kind === 'number' ? kind : 200;
  const path = typeof kind === 'number' ? `http/${kind}` : kind;
  const response = await apiClient.post<{ code?: string; message: string; details?: { synthetic?: boolean; status?: number } }>(`/api/v1/admin/system-alert/test/${path}`, undefined, {
    timeout: 15000,
    validateStatus: status => status === expectedStatus,
  });
  const expectedBody = typeof kind === 'number'
    ? response.data?.code === 'SYNTHETIC_HTTP_ERROR' && response.data?.details?.synthetic === true && response.data?.details?.status === kind
    : response.data?.message === 'error log emitted';

  if (response.status !== expectedStatus || !expectedBody) {
    throw new ApiError('The backend returned an unexpected test response.', response.status);
  }
}

export const getBackendErrors = (filter: TimeFilter, service: string, signal?: AbortSignal) =>
  monitorRequest<BackendErrors>('GET', '/api/v1/backend/errors', signal, { ...filter, service });

export const getBackendLogs = (kind: BackendLogKind, filter: TimeFilter, service: string, cursor: string | undefined, limit = 10, signal?: AbortSignal) => {
  const range = 'window' in filter ? { window: filter.window } : { from: filter.from, to: filter.to };
  return monitorRequest<BackendLogsPage>('GET', `/api/v1/backend/logs/${kind}`, signal,
    cursor ? { cursor, limit } : { ...range, service, limit });
};
